import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { IdeGrant, IdeGrantLevel } from "~/api/ideTypes";
import { useApproveIdeGrant, useDeclineIdeGrant } from "~/api/queries";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";

const DEFAULT_EXPIRY_DAYS = 7;
const MAX_EXPIRY_DAYS = 90;

/**
 * What each rung actually means, in one sentence — §E-2.
 *
 * A `flow-run` grant lets a DIFFERENT PERSON run an agent that reads, writes and executes
 * inside the owner's filesystem, spending the owner's tokens, under the owner's machine's
 * permissions. Saying "flow-run" and leaving it there is not consent; saying what it does is.
 */
const LEVEL_MEANING: Record<IdeGrantLevel, string> = {
  read: "Can look at files under the scope.",
  write: "Can change them.",
  "flow-run": "Can run a whole flow graph on that machine.",
};

/**
 * The owner's answer to a pending request: level, scope and expiry, decided together.
 *
 * Narrowest by default — `read`, no path scope, 7 days — because a default is a decision most
 * people will accept without reading, and the safe one is the only honest default here.
 */
export function GrantReviewRow({ grant }: { grant: IdeGrant }) {
  const { t } = useTranslation();
  const [level, setLevel] = useState<IdeGrantLevel>("read");
  const [pathScope, setPathScope] = useState("");
  const [days, setDays] = useState(DEFAULT_EXPIRY_DAYS);
  const approve = useApproveIdeGrant();
  const decline = useDeclineIdeGrant();

  const submit = () => {
    const clamped = Math.min(Math.max(Math.round(days) || DEFAULT_EXPIRY_DAYS, 1), MAX_EXPIRY_DAYS);
    approve.mutate(
      {
        grantId: grant.id,
        level,
        ...(pathScope.trim() ? { scope: { workspacePathPrefix: pathScope.trim() } } : {}),
        expiresAt: new Date(Date.now() + clamped * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        onSuccess: () => toast.success(t("Access granted")),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : t("Could not grant access")),
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-sm bg-bg-secondary p-3">
      <div className="flex flex-wrap items-end gap-2">
        <Select
          label={t("Level")}
          value={level}
          onChange={(event) => setLevel(event.target.value as IdeGrantLevel)}
        >
          <option value="read">{t("Read-only")}</option>
          <option value="write">{t("Write")}</option>
          <option value="flow-run">{t("Flow-run")}</option>
        </Select>
        <div className="min-w-[200px] flex-1">
          <Input
            label={t("Path scope (blank = all)")}
            value={pathScope}
            dir="ltr"
            onChange={(event) => setPathScope(event.target.value)}
          />
        </div>
        <div className="w-28">
          <Input
            label={t("Expires in (days)")}
            type="number"
            min={1}
            max={MAX_EXPIRY_DAYS}
            value={String(days)}
            onChange={(event) => setDays(Number(event.target.value))}
          />
        </div>
      </div>

      <p className="text-small text-text-secondary">{t(LEVEL_MEANING[level])}</p>

      {level === "flow-run" ? (
        <p className="text-small text-warning">
          {t(
            "Flow-run lets this person run an agent that reads, writes and executes on your machine, spending your tokens.",
          )}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button size="sm" disabled={approve.isPending} onClick={submit}>
          {t("Approve")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={decline.isPending}
          onClick={() =>
            decline.mutate(grant.id, {
              onSuccess: () => toast.success(t("Request declined")),
              onError: (error) =>
                toast.error(error instanceof Error ? error.message : t("Could not decline")),
            })
          }
        >
          {t("Decline")}
        </Button>
      </div>
    </div>
  );
}
